import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Breakpoint, CircularProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import ja from 'dayjs/locale/ja';
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import useResponsive from "../../hooks/useResponsive";
import { Path as RoutePath } from "../../path";
import { customerLabelName } from "../../statics";
import { actionCreators, ApplicationState } from "../../store";
import { formatNumberNullToHyphen, parseDate } from "../../utils";
import FetchHelper from "../FetchHelper";

const DEMAND_DRIVE_LIST = "DEMAND_DRIVE_LIST";
// -------------
// Props
export type DemandDriveListProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;
// -------------
// Component
export const DemandDriveList = (props: DemandDriveListProps) => {
    // #region properties
    const { onError, onSearch }   = props;
    const [loadingFlag, setLoadingFlag] = useState<boolean>(false);
    const [demandDriveList, setDemandDriveList] = useState<any>([])
    // ソート用のカラム定義
    const [columns] = useState([
        { key: 'develop', label: `Test` },
        { key: 'customer_name', label: `${customerLabelName}` },
        { key: 'th2nd_kw', label: '限界警報\n(kW)' },
        { key: 'current_kw', label: '現在電力\n(kW)' },
        { key: 'day_max_kw', label: '当日最大需要\n電力(kW)' },
        { key: 'day_sum_kwh', label: '当日積算電力量\n(kWh)' },
        { key: 'month_max_kw', label: '当月最大需要電力\n(kW)' },
        { key: 'month_max_time', label: '当月最大電力日時' },
    ]);
    // --- ソート状態 ---
    // 3つの状態: null（ソートなし）、asc（昇順）、desc（降順）
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const isMobile = useResponsive({ query: 'between', key: "sm" as Breakpoint,start:320,end:766 });
    let navigate = useNavigate();
    localStorage.setItem('lastActiveFormPath', RoutePath.DemandDrive);
    localStorage.setItem('title', 'スマートデマンドライブ');
    const colHeaderStyle = {
        fontWeight: 'bold',
        border: '1px solid gray',
        textAlign: 'left' as 'left',
        backgroundColor: '#d9d9d9',
        position: 'relative',
        marginTop: "8px",
        marginBottom: "8px",
        paddingLeft: '10px',
        // アイコン分のスペースを常に確保
    }

    const colHeaderMobileStyle = {
        fontWeight: 'bold',
        border: '1px solid gray',
        textAlign: 'left' as 'left',
        backgroundColor: '#d9d9d9',
        fontSize: '0.5rem',
        position: 'relative',
        height: '20px'
    }

    const colStyle = {
        border: '1px solid gray',
        textAlign: 'left' as 'left',
        paddingLeft: '10px',
        height: '44px',
    }

    const colMobileStyle = {
        border: '1px solid gray',
        textAlign: 'left' as 'left',
        height: '44px',
        fontSize: '0.6rem',
    }

    const getStylePowerOverload = (val: number, th2nd_kw: number) => {
        if(val > th2nd_kw){
            return { color: 'red', fontWeight: 'bold' };
        }
        return {};
    }

    const getStyleNull = (val: any) => {
        if(val === null){
            return {backgroundColor: '#FFFFCC', color: "red"}; // 値がnullの場合は背景を薄いグレーに
        }
        if(val == 0){
            return {backgroundColor: '#FFFFCC', color: "black"}; // 値がない場合は背景を薄いグレーに
        }
        return {};
    }

    // データのソート処理
    const sortedData = useMemo(() => {
        if (!sortConfig) return demandDriveList.live_situation_info_array;
        const sorted = [...demandDriveList.live_situation_info_array].sort((a: any, b: any) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            // customer_name の場合は常に文字列として比較
            if (sortConfig.key === "customer_name") {
                // null/undefined 対応
                const aStr = aVal ?? '';
                const bStr = bVal ?? '';
                if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            }
            // month_max_time の場合は日付として比較（例: "3月27日 13:00"）
            if (sortConfig.key === "month_max_time") {
                const parseDate = (val: string) => {
                    if (!val) return null;
                    // "3月27日 13:00" → 月・日・時・分を抽出
                    const match = val.match(/(\d+)月(\d+)日\s+(\d+):(\d+)/);
                    if (!match) return null;
                    const [, month, day, hour, minute] = match;
                    // 年は仮で現在年を使用
                    const year = new Date().getFullYear();
                    return new Date(
                        year,
                        Number(month) - 1,
                        Number(day),
                        Number(hour),
                        Number(minute)
                    ).getTime();
                };
                const aTime = parseDate(aVal);
                const bTime = parseDate(bVal);
                // null（パース不可）は空扱い
                if (aTime === null && bTime === null) return 0;
                if (aTime === null) return sortConfig.direction === 'asc' ? -1 : 1;
                if (bTime === null) return sortConfig.direction === 'asc' ? 1 : -1;
                return sortConfig.direction === 'asc'
                    ? aTime - bTime
                    : bTime - aTime;
            }
            // 空または数値でない値を処理: 昇順の場合は先頭、降順の場合は末尾に配置
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            const aEmpty = aVal === undefined || aVal === null || aVal === '' || isNaN(aNum);
            const bEmpty = bVal === undefined || bVal === null || bVal === '' || isNaN(bNum);
            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return sortConfig.direction === 'asc' ? -1 : 1;
            if (bEmpty) return sortConfig.direction === 'asc' ? 1 : -1;
            // 数値で比較
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            // 数値でない場合は文字列で比較
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [demandDriveList.live_situation_info_array, sortConfig]);
    // #endregion

    // #region useEffect
    useEffect(() => {
        setLoadingFlag(true);
        onSearch();
    }, []);
    // #endregion

    // #region functions
    // ソートハンドラー
    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (!prev || prev.key !== key) {
                // 新しいカラムを昇順でソート
                return { key, direction: 'asc' };
            } else if (prev.direction === 'asc') {
                // 昇順の場合は降順に変更
                return { key, direction: 'desc' };
            } else if (prev.direction === 'desc') {
                // 降順の場合はソート解除
                return null;
            }
            return null;
        });
    };

    // 取得正常終了時の初期表示
    const handleSuccess = (data: any) => {
        setDemandDriveList(data);
        setLoadingFlag(false);
    }

    const handleError = (success: boolean, data: any, error: any) => {
        onError(success, data, error);
        setLoadingFlag(false);
    }

    const getNowTime = () => {
        dayjs.locale(ja);
        let now = dayjs();
        return (
            <span style={{ letterSpacing: '0.3em' }}>{now.locale('ja').format('YYYY年MM月DD日 (ddd) HH時mm分')}</span>
        );
    }

    const handleOnclick = (mainid: string) => {
        sessionStorage.setItem('references', window.location.pathname);
        navigate(RoutePath.Gaex1 + "?mainid=" + mainid);
    }

    // ソート用アイコン: 昇順は▲、降順は▼、アニメーションなし
    const SortIcon = ({ active, direction }: { active: boolean, direction?: 'asc' | 'desc' }) => {
        // アイコンのスペースを常に確保するため、非active時も空のspanを返す
        const iconSize = isMobile ? '0.6em' : '1em';
        const iconStyle = {
            verticalAlign: 'middle',
            height: iconSize,
            width: iconSize,
            display: 'inline-flex',
            alignItems: 'center',
            padding: 0
        };
        if (active && direction === 'asc') {
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#3b3b3b', verticalAlign: 'middle', height: iconSize, width: iconSize }}>
                    <ArrowUpwardIcon fontSize="small" sx={iconStyle} />
                </span>
            );
        } else if (active && direction === 'desc') {
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#3b3b3b', verticalAlign: 'middle', height: iconSize, width: iconSize }}>
                    <ArrowDownwardIcon fontSize="small" sx={iconStyle} />
                </span>
            );
        }
        // 非active時は空spanで幅を確保
        return <span style={{ display: 'inline-block', marginLeft: 4, height: iconSize, width: iconSize }} />;
    };
    // #endregion

    return (
        <>
            <div style={{ height: '100%', width: '100%' }}>
                {
                    isMobile ? 
                    <>
                        <Stack style={{fontSize:"0.95rem"}} direction="row" justifyContent="end" alignItems="center" sx={{ mb: 2 }}>
                            {getNowTime()}
                        </Stack>
                    </>
                    :
                    <Stack  direction="row" justifyContent="end" alignItems="center" sx={{ mb: 2 }}>
                        {getNowTime()}
                    </Stack>
                }
                {loadingFlag ? (
                    <div style={{ top: "0px", left: '0px', position: "fixed", zIndex: 1200, width: '100%', height: '100%', padding: '50vh 50% 50% 50%', background: '#00000030' }}>
                        <CircularProgress />
                    </div>
                ) : <>
                    <div style={{ marginTop: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
                            <tbody>
                                <tr>
                                    {
                                        isMobile ?
                                        <>
                                        {columns.map((col, index) => (
                                                <td
                                                    key={col.key}
                                                    style={{ 
                                                        ...colHeaderMobileStyle, cursor: 'pointer', userSelect: 'none', position: 'relative', whiteSpace: 'pre-wrap',
                                                        maxWidth: index === 0 ? "55px" :'', // 最初の列は狭くする
                                                     }}
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    {col.label}
                                                    {
                                                        (
                                                        <SortIcon
                                                            active={sortConfig?.key === col.key}
                                                            direction={sortConfig?.key === col.key ? sortConfig?.direction : undefined}
                                                        />
                                                        )
                                                    }
                                                </td>
                                            ))}
                                        </> :
                                        <>
                                            {columns.map((col,index) => (
                                                <td
                                                    key={col.key}
                                                    style={{ 
                                                        ...colHeaderStyle, cursor: 'pointer', userSelect: 'none', position: 'relative', whiteSpace: 'pre-wrap',
                                                        maxWidth: index === 0 ? "55px" :'', // 最初の列は狭くする
                                                    }}
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    {col.label}
                                                    {
                                                        (
                                                            <SortIcon
                                                                active={sortConfig?.key === col.key}
                                                                direction={sortConfig?.key === col.key ? sortConfig?.direction : undefined}
                                                            />
                                                        )
                                                    }
                                                </td>
                                            ))}
                                        </>
                                    }
                                </tr>
                                {
                                    isMobile ?
                                        <>
                                        {
                                            sortedData && sortedData.map((val:any, index:any) =>
                                                <tr key={index} className="row-slide-fade-anim">
                                                    <td style={{
                                                        border: '1px solid gray',
                                                        height: '44px',
                                                        textAlign: 'center',
                                                        maxWidth: "30px"
                                                    }}>
                                                        {val.develop == null || val.develop == 0 ? '' : '〇'}
                                                    </td>
                                                    <td style={{
                                                        border: '1px solid gray',
                                                        height: '44px',
                                                        textAlign: 'left',
                                                    }}>
                                                        <Typography style={{ textDecoration: 'underline', color: 'blue', display: 'inline' }}>
                                                            <span style={{textAlign: 'left' as 'left', height: '44px', fontSize: '0.6rem', cursor: 'pointer'}} onClick={() => handleOnclick(val.mainid)}>{val.customer_name}</span>
                                                        </Typography>
                                                    </td>
                                                    <td style={{...colMobileStyle, minWidth: "36px"}}>{formatNumberNullToHyphen(val.th2nd_kw, "kW")}</td>
                                                    <td style={{...colMobileStyle, ...getStylePowerOverload(val.current_kw, val.th2nd_kw), ...getStyleNull(val.current_kw), minWidth: "40px"}}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                    <td style={{...colMobileStyle, ...getStylePowerOverload(val.day_max_kw, val.th2nd_kw), ...getStyleNull(val.day_max_kw), minWidth: "52px"}}>{formatNumberNullToHyphen(val.day_max_kw, "kW")}</td>
                                                    <td style={{...colMobileStyle, ...getStyleNull(val.day_sum_kwh),  minWidth: "60px"}}>{formatNumberNullToHyphen(val.day_sum_kwh, "kWh")}</td>
                                                    <td style={{...colMobileStyle, ...getStylePowerOverload(val.month_max_kw, val.th2nd_kw), ...getStyleNull(val.month_max_kw), minWidth: "70px" }}>{formatNumberNullToHyphen(val.month_max_kw, "kW")}</td>
                                                    <td style={{...colMobileStyle, ...getStyleNull(val.month_max_time), minWidth: "36px"}}>{formatNumberNullToHyphen(val.month_max_time, "")}</td>
                                                </tr>
                                            )
                                        }
                                        </>
                                    : 
                                    <>
                                    {
                                        sortedData && sortedData.map((val:any, index:any) =>
                                            <tr key={index} className="row-slide-fade-anim">
                                                <td style={{
                                                    border: '1px solid gray',
                                                    height: '44px',
                                                    textAlign: 'center',
                                                    maxWidth: "30px"
                                                }}>
                                                    {val.develop == null || val.develop == 0 ? '' : '〇'}
                                                </td>
                                                <td style={{
                                                    border: '1px solid gray',
                                                    paddingLeft: '10px',
                                                    height: '44px',
                                                    textAlign: 'left'
                                                }}>
                                                    <Typography style={{ textDecoration: 'underline', color: 'blue', display: 'inline' }}>
                                                        <span style={{ cursor: 'pointer' }} onClick={() => handleOnclick(val.mainid)}>{val.customer_name}</span>
                                                    </Typography>
                                                </td>
                                                <td style={colStyle}>{formatNumberNullToHyphen(val.th2nd_kw, "kW")}</td>
                                                <td style={{...colStyle, ...getStylePowerOverload(val.current_kw, val.th2nd_kw), ...getStyleNull(val.current_kw)}}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                <td style={{...colStyle, ...getStylePowerOverload(val.day_max_kw, val.th2nd_kw), ...getStyleNull(val.day_max_kw)}}>{formatNumberNullToHyphen(val.day_max_kw, "kW")}</td>
                                                <td style={{...colStyle, ...getStyleNull(val.day_sum_kwh)}}>{formatNumberNullToHyphen(val.day_sum_kwh, "kWh")}</td>
                                                <td style={{...colStyle, ...getStylePowerOverload(val.month_max_kw, val.th2nd_kw), ...getStyleNull(val.month_max_kw)}}>{formatNumberNullToHyphen(val.month_max_kw, "kW")}</td>
                                                <td style={{...colStyle, ...getStyleNull(val.month_max_time)}}>{formatNumberNullToHyphen(val.month_max_time, "")}</td>
                                            </tr>
                                        )
                                    }
                                    </>
                                }
                                
                            </tbody>
                        </table>
                    </div>
                </>}
            </div>
            <FetchHelper fetchId={DEMAND_DRIVE_LIST} onComplete={(success, data, error) => success ? handleSuccess(data) : handleError(success, data, error)} />
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
});

const mapDispatchToProps = {
    onSearch: () => actionCreators.fetch(DEMAND_DRIVE_LIST, `/telemetry/get?code=GAEX6`, "GET", null),
    onError: (success: boolean, data: any, error: any) =>
        actionCreators.showMessage({
            type: "error",
            title: "スマートデマンドライブ",
            body: "スマートデマンドライブ一覧取得出来ません。",
        }),
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DemandDriveList as any);
